import { useEffect, useState } from 'react';
import { audioQualityIssues, measureAudioQuality, type AudioQualityIssue, type AudioQualityMetrics } from '../audio-quality';

export type AudioQualityState =
  | { status: 'idle' }
  | { status: 'measuring' }
  /** The browser could not decode the recording: sent without metrics, as before. */
  | { status: 'unavailable' }
  | { status: 'ready'; metrics: AudioQualityMetrics; issues: AudioQualityIssue[] };

async function decode(blob: Blob) {
  const context = new AudioContext();
  try {
    const buffer = await context.decodeAudioData(await blob.arrayBuffer());
    return measureAudioQuality(buffer.getChannelData(0), buffer.sampleRate);
  } finally {
    void context.close();
  }
}

/** Spec 006 (D5): measures each new recording once it is finished. */
export function useAudioQuality(blob: Blob | null): AudioQualityState {
  const [state, setState] = useState<AudioQualityState>({ status: 'idle' });

  useEffect(() => {
    if (!blob) {
      setState({ status: 'idle' });
      return undefined;
    }
    let current = true;
    setState({ status: 'measuring' });
    decode(blob).then(
      (metrics) => current && setState({ status: 'ready', metrics, issues: audioQualityIssues(metrics) }),
      () => current && setState({ status: 'unavailable' }),
    );
    return () => {
      current = false;
    };
  }, [blob]);

  return state;
}
